import React, { forwardRef, useImperativeHandle, useState } from 'react';
import './actionModal.scss';
import { Button, Modal, Input, notification } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import { FileIcon, CancelIcon } from 'components/Elements/CustomSVGIcon';
import FileUploader from 'components/Form/FileUploader';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';

const { TextArea } = Input;

/**
 * Action types supported by the modal
 */
export type ActionType = 'approve' | 'cost_approve' | 'reject' | 'query' | 'cancel';

/**
 * Actions that require a message to be entered
 */
const MESSAGE_REQUIRED_ACTIONS: ActionType[] = ['reject', 'query', 'cancel'];

/**
 * Actions that support file uploads and optional notes
 */
const APPROVAL_ACTIONS: ActionType[] = ['approve', 'cost_approve'];

/**
 * Configuration for an action modal
 */
interface ActionProps {
    /** Status ID associated with this action */
    statusId: number;
    /** Modal title */
    title: string;
    /**
     * Subtitle shown above the message textarea.
     * For reject/query/cancel actions: Shows the textarea and message is required.
     * For approve/cost_approve actions: Not typically used (notes field is shown instead).
     */
    subTitle?: string;
    /** Description text shown in the modal body */
    description?: string;
    /** Text for the confirm button */
    confirmButton: string;
    /** Text for the cancel button */
    cancelButton: string;
    /** The action type to be performed */
    action: ActionType | string;
    /** Placeholder text for the message textarea */
    placeholder?: string;
}

/**
 * Parameters passed to the onConfirm callback
 */
export interface ActionModalConfirmParams {
    /** The action type being confirmed */
    action: ActionType | string | undefined;
    /** Optional message entered by the user (for reject/query actions) */
    message?: string;
    /** Optional files uploaded (for approve/cost_approve actions) */
    files?: UploadFile[];
    /** Optional approval notes (for approve/cost_approve actions) */
    notes?: string;
}

/**
 * Callback signature for the onConfirm handler.
 *
 * @param action - The action type (e.g., 'approve', 'reject')
 * @param message - User-entered message (required for reject/query, optional otherwise)
 * @param files - Uploaded files (only for approve/cost_approve actions)
 * @param notes - Approval notes (only for approve/cost_approve actions)
 */
type OnConfirmCallback = (
    action: ActionType | string | undefined,
    message?: string,
    files?: UploadFile[],
    notes?: string
) => void;

interface ActionModalProps {
    /** Controls modal visibility */
    visible: boolean;
    /** Action configuration, or null when modal should be hidden */
    action: ActionProps | null;
    /**
     * Callback fired when user confirms the action.
     * For approve/cost_approve: receives action, undefined message, files array, and notes string.
     * For reject/query/cancel: receives action and message string.
     */
    onConfirm: OnConfirmCallback;
    /** Callback fired when user cancels/closes the modal */
    onCancel: () => void;
    /** Hide the supporting documentation upload section */
    hideAttachments?: boolean;
}

/**
 * Imperative handle methods exposed via ref
 */
export interface ActionModalHandlers {
    /** Clears all form state (message, notes, files) */
    clear(): void;
}

const ActionModal: React.ForwardRefRenderFunction<ActionModalHandlers, ActionModalProps> = ({ visible, onConfirm, onCancel, action, hideAttachments = false }, ref) => {
    const [value, setValue] = React.useState<string>('');
    const [approvalNotes, setApprovalNotes] = useState<string>('');
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    useImperativeHandle(ref, () => ({
        clear: () => {
            setValue('');
            setApprovalNotes('');
            setFileList([]);
        },
    }));

    // Determine action type for validation and UI
    const currentAction = action?.action as ActionType | undefined;
    const isApproveAction = currentAction && APPROVAL_ACTIONS.includes(currentAction);
    const isMessageRequiredAction = currentAction && MESSAGE_REQUIRED_ACTIONS.includes(currentAction);

    // Message is required for reject/query/cancel actions when the textarea is shown
    const messageRequired = isMessageRequiredAction && action?.subTitle;

    const handleFileUploadChange = (files: UploadFile[]) => {
        setFileList(files);
    };

    const handleRemoveFile = (uid: string) => {
        setFileList(fileList.filter(file => file.uid !== uid));
    };

    /**
     * Validate and submit the form.
     *
     * Validation rules:
     * - For reject/query/cancel: Message is REQUIRED when subTitle is present
     * - For approve/cost_approve: Notes and files are OPTIONAL
     */
    const handleSendMessage = () => {
        // Validate required message for reject/query/cancel actions
        if (messageRequired && !value.trim()) {
            notification.error({
                message: 'Message Required',
                description: `Please enter a ${currentAction === 'query' ? 'query' : 'reason'} before submitting.`,
            });
            return;
        }

        // Submit the form
        onConfirm(action?.action, value, fileList, approvalNotes);

        // Clear form state after successful submission
        setValue('');
        setApprovalNotes('');
        setFileList([]);
    };

    return (
        <div className="action-box-container">
            <Modal
                open={visible}
                onCancel={() => onCancel()}
                footer={null}
                width={650}
                title={action?.title}
                wrapClassName="action-modal">
                <p>{action?.description}</p>
                {
                    action?.subTitle && (
                        <div>
                            <div>{action?.subTitle}</div>
                            <TextArea
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder={action?.placeholder}
                                maxLength={MAX_LONG_TEXT_LENGTH}
                                autoSize={{ minRows: 3, maxRows: 5 }} />
                        </div>
                    )
                }
                {isApproveAction && (
                    <div className="supporting-docs-section">
                        <div className="supporting-docs-label">Notes or Comments (Optional)</div>
                        <TextArea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Please add any additional notes here"
                            maxLength={MAX_LONG_TEXT_LENGTH}
                            className="approval-notes-input" />
                        {!hideAttachments && (
                            <>
                                <div className="supporting-docs-label" style={{ marginTop: 16 }}>Supporting Documentation (Optional)</div>
                                <FileUploader onChange={handleFileUploadChange} fileList={fileList} />
                                {fileList.length > 0 && (
                                    <ul className="uploaded-files-list-container">
                                        {fileList.map(file => (
                                            <li key={file.uid} className="uploaded-file-display">
                                                <div className="icon-wrapper">
                                                    <FileIcon />
                                                    <span>{file.name}</span>
                                                </div>
                                                <div className="remove-file-button" onClick={() => handleRemoveFile(file.uid)}>
                                                    <CancelIcon s="#C0D1D8" />
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                )}
                <div className="action-modal-buttons">
                    <Button type="primary" className="action-confirm-button" onClick={() => handleSendMessage()}>{action?.confirmButton}</Button>
                    <Button type="default" className="action-cancel-button" onClick={() => onCancel()}>{action?.cancelButton}</Button>
                </div>
            </Modal>
        </div>
    );
};

export default forwardRef(ActionModal);
